import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Table,
  Form,
  InputGroup,
  Modal,
  Alert,
  Spinner,
  Badge,
  Pagination,
} from 'react-bootstrap';
import { 
  PlusCircle, 
  Search, 
  Pencil, 
  Trash, 
  CheckCircle, 
  XCircle, 
  ArrowRepeat,
  Building,
  FileText,
  Globe,
  ChevronLeft,
  ChevronRight,
  ChevronDoubleLeft,
  ChevronDoubleRight
} from 'react-bootstrap-icons';
import axios from 'axios';
import { API_BASE_URL } from '../constants/api_constants';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';

const FormesJuridiques = () => {
  // ==================== ÉTATS ====================
  const [formes, setFormes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  
  // États pour le modal
  const [showModal, setShowModal] = useState(false);
  const [editingForme, setEditingForme] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    libelle: '',
    pays: '',
    description: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  
  // États pour la confirmation de suppression
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingForme, setDeletingForme] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ==================== AUTH ====================
  const getAuthToken = () => sessionStorage.getItem('authToken');
  
  const getHeaders = () => {
    const token = getAuthToken();
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  };

  // ==================== CHARGEMENT AVEC PAGINATION ====================
  const loadFormes = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_BASE_URL}/formes?search=${search}&page=${page}&per_page=${itemsPerPage}`,
        { headers: getHeaders() }
      );
      
      // Si l'API renvoie une structure paginée
      if (response.data && response.data.data) {
        setFormes(response.data.data);
        setTotalItems(response.data.total || response.data.data.length);
      } else {
        // Sinon, on pagine côté client
        setFormes(response.data);
        setTotalItems(response.data.length);
      }
      setCurrentPage(page);
    } catch (error) {
      console.error('Erreur chargement:', error);
      showToast('Erreur lors du chargement des formes juridiques', 'danger');
    } finally {
      setLoading(false);
    }
  }, [search, itemsPerPage]);

  // ==================== FILTRAGE CLIENT ====================
  const getFilteredFormes = useCallback(() => {
    if (!search.trim()) {
      return formes;
    }
    
    const searchLower = search.toLowerCase();
    return formes.filter(forme => 
      (forme.libelle || '').toLowerCase().includes(searchLower) ||
      (forme.code || '').toLowerCase().includes(searchLower) ||
      (forme.pays || '').toLowerCase().includes(searchLower) ||
      (forme.description || '').toLowerCase().includes(searchLower)
    );
  }, [formes, search]);

  // ==================== PAGINATION CLIENT ====================
  const getPaginatedFormes = useCallback(() => {
    const filtered = getFilteredFormes();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  }, [getFilteredFormes, currentPage, itemsPerPage]);

  const totalFiltered = getFilteredFormes().length;
  const totalPages = Math.ceil(totalFiltered / itemsPerPage) || 1;
  const currentFormes = getPaginatedFormes();

  // ==================== EFFETS ====================
  useEffect(() => {
    loadFormes(1);
  }, []);

  // Réinitialiser la page quand la recherche change
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  // ==================== CRUD ====================
  const handleCreate = () => {
    setEditingForme(null);
    setFormData({
      code: '',
      libelle: '',
      pays: '',
      description: '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (forme) => {
    setEditingForme(forme);
    setFormData({
      code: forme.code || '',
      libelle: forme.libelle || '',
      pays: forme.pays || '',
      description: forme.description || '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setFormErrors({});
      
      const url = editingForme 
        ? `${API_BASE_URL}/formes/${editingForme.id}`
        : `${API_BASE_URL}/formes`;
      
      const method = editingForme ? 'put' : 'post';
      
      const dataToSend = {
        code: formData.code.toUpperCase().trim(),
        libelle: formData.libelle.trim(),
        pays: formData.pays.trim(),
        description: formData.description?.trim() || '',
      };
      
      const response = await axios({
        method,
        url,
        data: dataToSend,
        headers: getHeaders(),
      });
      
      if (response.status === 200 || response.status === 201) {
        showToast(
          editingForme 
            ? 'Forme juridique mise à jour avec succès'
            : 'Forme juridique créée avec succès',
          'success'
        );
        setShowModal(false);
        loadFormes(currentPage);
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      } else if (error.response?.data?.message) {
        showToast(error.response.data.message, 'danger');
      } else {
        showToast('Erreur lors de la sauvegarde', 'danger');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingForme) return;
    
    try {
      setDeleting(true);
      
      const response = await axios.delete(
        `${API_BASE_URL}/formes/${deletingForme.id}`,
        { headers: getHeaders() }
      );
      
      if (response.status === 200) {
        showToast('Forme juridique supprimée avec succès', 'success');
        setShowDeleteModal(false);
        setDeletingForme(null);
        loadFormes(Math.min(currentPage, totalPages));
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Erreur lors de la suppression';
      showToast(message, 'danger');
    } finally {
      setDeleting(false);
    }
  };

  // ==================== PAGINATION ====================
  const renderPagination = () => {
    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    // Bouton "Premier"
    items.push(
      <Pagination.First 
        key="first" 
        onClick={() => setCurrentPage(1)} 
        disabled={currentPage === 1}
      />
    );
    
    // Bouton "Précédent"
    items.push(
      <Pagination.Prev 
        key="prev" 
        onClick={() => setCurrentPage(currentPage - 1)} 
        disabled={currentPage === 1}
      />
    );

    // Pages
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item 
          key={i} 
          active={i === currentPage} 
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Pagination.Item>
      );
    }

    // Bouton "Suivant"
    items.push(
      <Pagination.Next 
        key="next" 
        onClick={() => setCurrentPage(currentPage + 1)} 
        disabled={currentPage === totalPages}
      />
    );
    
    // Bouton "Dernier"
    items.push(
      <Pagination.Last 
        key="last" 
        onClick={() => setCurrentPage(totalPages)} 
        disabled={currentPage === totalPages}
      />
    );

    return items;
  };

  // ==================== TOAST ====================
  const [toast, setToast] = useState({ show: false, message: '', variant: 'success' });
  
  const showToast = (message, variant = 'success') => {
    setToast({ show: true, message, variant });
    setTimeout(() => setToast({ show: false, message: '', variant: 'success' }), 5000);
  };

  // ==================== RENDU ====================
  
  if (loading && !formes.length) {
  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ minHeight: '100vh', background: '#f0f2f5' }}>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '70vh' }}>
          <Card className="border-0 shadow-lg rounded-4" style={{ maxWidth: '480px', width: '100%' }}>
            <Card.Body className="p-5 text-center">
              {/* Animation du bâtiment */}
              <div className="mb-4">
                <div 
                  className="mx-auto d-flex align-items-center justify-content-center"
                  style={{
                    width: '80px',
                    height: '80px',
                    background: 'linear-gradient(135deg, #0d6efd, #0a58ca)',
                    borderRadius: '20px',
                    boxShadow: '0 10px 40px rgba(13, 110, 253, 0.3)',
                    animation: 'pulse 1.5s ease-in-out infinite'
                  }}
                >
                  <Building size={40} className="text-white" />
                </div>
              </div>

              <h5 className="fw-bold mb-2" style={{ color: '#1a1a2e' }}>
                Chargement des formes juridiques
              </h5>
              <p className="text-muted mb-4" style={{ fontSize: '0.9rem' }}>
        Veuillez patienter pendant que nous récupérons les données...
              </p>

              {/* Barre de progression animée */}
              <div className="mx-auto" style={{ width: '80%' }}>
                <div className="progress" style={{ height: '4px', borderRadius: '2px', background: '#e9ecef' }}>
                  <div 
                    className="progress-bar progress-bar-striped progress-bar-animated"
                    style={{
                      width: '100%',
                      background: 'linear-gradient(90deg, #0d6efd, #0a58ca)',
                      borderRadius: '2px'
                    }}
                  />
                </div>
              </div>

              {/* Points d'animation */}
              <div className="d-flex justify-content-center gap-2 mt-4">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="rounded-circle"
                    style={{
                      width: '8px',
                      height: '8px',
                      background: '#0d6efd',
                      animation: `dotPulse 1.5s ease-in-out ${i * 0.15}s infinite`,
                      opacity: 0.3
                    }}
                  />
                ))}
              </div>

              <div className="mt-4">
                <small className="text-muted">
                  <span className="me-1">⏳</span>
                  Cette opération peut prendre quelques secondes
                </small>
              </div>
            </Card.Body>
          </Card>
        </div>

        <style>{`
          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
              box-shadow: 0 10px 40px rgba(13, 110, 253, 0.3);
            }
            50% {
              transform: scale(1.05);
              box-shadow: 0 10px 60px rgba(13, 110, 253, 0.5);
            }
          }
          
          @keyframes dotPulse {
            0%, 100% {
              transform: scale(1);
              opacity: 0.3;
            }
            50% {
              transform: scale(1.5);
              opacity: 1;
            }
          }
        `}</style>

        <Footer />
      </div>
    </>
  );
}
  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ minHeight: '100vh', background: '#f0f2f5' }}>
        <div className="container-fluid">
          {/* Toast */}
          {toast.show && (
            <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 9999 }}>
              <Alert 
                variant={toast.variant} 
                onClose={() => setToast({ ...toast, show: false })}
                dismissible
                style={{ minWidth: '300px' }}
              >
                {toast.message}
              </Alert>
            </div>
          )}

          {/* En-tête */}
          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                <div className="mb-3 mb-md-0">
                  <ol className="breadcrumb fs-sm mb-1">
                    <li className="breadcrumb-item">
                      <a href="#" onClick={(e) => e.preventDefault()}>Paramètres</a>
                    </li>
                    <li className="breadcrumb-item active">Formes juridiques</li>
                  </ol>
                  <h4 className="main-title mb-0 d-flex align-items-center">
                    <Building className="me-2 text-primary" size={28} />
                    Formes juridiques
                  </h4>
                  <p className="text-muted mb-0">Gestion des formes juridiques des contribuables</p>
                </div>

                <button 
                  className="btn btn-primary d-flex align-items-center shadow-sm"
                  onClick={handleCreate}
                >
                  <PlusCircle size={18} className="me-2" />
                  Nouvelle forme
                </button>
              </div>
            </Col>
          </Row>

          {/* Filtres */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="border-0 shadow-sm rounded-3">
                <Card.Body>
                  <Row className="g-3">
                    
                    <Col md={8}>
                       <Form.Label className="fw-bold small">Recherche</Form.Label>
                      <InputGroup>
                        <InputGroup.Text className="bg-light border-0">
                          <Search />
                        </InputGroup.Text>
                        <Form.Control
                          placeholder="Rechercher par libellé, code, pays ou description..."
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          className="bg-light border-0"
                        />
                        {search && (
                          <button 
                            className="btn btn-outline-secondary border-0"
                            onClick={() => setSearch('')}
                          >
                            <XCircle size={16} />
                          </button>
                        )}
                      </InputGroup>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-bold small">Par page</Form.Label>
                        <Form.Select
                          value={itemsPerPage}
                          onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                          }}
                          className="bg-light border-0"
                        >
                          <option value="5">5</option>
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    {/* <Col md={3} className="d-flex justify-content-end align-items-center">
                      <button
                        className="btn btn-outline-primary btn-sm d-flex align-items-center"
                        onClick={() => loadFormes(currentPage)}
                      >
                        <ArrowRepeat size={14} className="me-2" />
                        Actualiser
                      </button>
                    </Col> */}
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Tableau */}
          <Row>
            <Col md={12}>
              <Card className="shadow-sm border-0 rounded-3">
                <Card.Header className="bg-primary d-flex justify-content-between align-items-center py-3 border-bottom">
                  <div className="d-flex align-items-center">
                    <FileText className="me-2 text-white" size={18} />
                    <span className="fw-semibold text-white">
                      Liste des formes juridiques ({totalFiltered})
                    </span>
                    {search && (
                      <Badge bg="info" className="ms-2 rounded-pill">
                        Filtre actif
                      </Badge>
                    )}
                  </div>
                  <div className="text-white small">
                    Page {currentPage} / {totalPages}
                  </div>
                </Card.Header>
                
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table hover className="mb-0 align-middle">
                      <thead className="bg-light">
                        <tr>
                          <th style={{ width: '50px' }}>#</th>
                          <th>Code</th>
                          <th>Libellé</th>
                          <th>Pays</th>
                          <th>Description</th>
                          <th style={{ width: '150px' }} className="text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentFormes.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="text-center py-5">
                              <Building size={48} className="text-muted opacity-25 mb-3" />
                              <h5 className="text-muted">
                                {search ? 'Aucun résultat trouvé' : 'Aucune forme juridique'}
                              </h5>
                              <p className="text-muted mb-0">
                                {search 
                                  ? 'Essayez avec d\'autres critères de recherche'
                                  : 'Commencez par créer une nouvelle forme juridique'
                                }
                              </p>
                              {!search && (
                                <button 
                                  className="btn btn-primary mt-3"
                                  onClick={handleCreate}
                                >
                                  <PlusCircle size={16} className="me-2" />
                                  Créer une forme
                                </button>
                              )}
                            </td>
                          </tr>
                        ) : (
                          currentFormes.map((forme, index) => {
                            const globalIndex = (currentPage - 1) * itemsPerPage + index + 1;
                            return (
                              <tr key={forme.id}>
                                <td className="text-center">{globalIndex}</td>
                                <td>
                                  <Badge bg="secondary" pill className="px-3 py-2">
                                    {forme.code || '-'}
                                  </Badge>
                                </td>
                                <td>
                                  <strong>{forme.libelle}</strong>
                                </td>
                                <td>
                                  {forme.pays ? (
                                    <span className="d-flex align-items-center">
                                      <Globe size={14} className="me-1 text-muted" />
                                      {forme.pays}
                                    </span>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                                <td>
                                  {forme.description || (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>
                                <td className="text-center">
                                  <button
                                    className="btn btn-outline-primary btn-sm me-2"
                                    onClick={() => handleEdit(forme)}
                                    style={{ width: '34px', height: '34px', padding: 0 }}
                                    title="Modifier"
                                  >
                                    <Pencil size={14} />
                                  </button>
                                  <button
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => {
                                      setDeletingForme(forme);
                                      setShowDeleteModal(true);
                                    }}
                                    style={{ width: '34px', height: '34px', padding: 0 }}
                                    title="Supprimer"
                                  >
                                    <Trash size={14} />
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {totalFiltered > 0 && (
                    <div className="p-3 border-top d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3">
                      <div className="text-muted small">
                        Affichage de <strong>{(currentPage - 1) * itemsPerPage + 1}</strong> à{' '}
                        <strong>{Math.min(currentPage * itemsPerPage, totalFiltered)}</strong> sur{' '}
                        <strong>{totalFiltered}</strong> forme(s)
                        {search && ` (filtré sur ${formes.length} total)`}
                      </div>
                      <div className="d-flex align-items-center gap-3">
                        <Pagination className="mb-0">
                          {renderPagination()}
                        </Pagination>
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>

        {/* Modals (inchangés) */}
        <Modal 
          show={showModal} 
          onHide={() => setShowModal(false)}
          centered
          size="lg"
        >
          <Modal.Header closeButton className="border-0" style={{ background: 'linear-gradient(135deg, #0d6efd, #0b5ed7)' }}>
            <Modal.Title className="text-white d-flex align-items-center">
              <Building className="me-2" size={24} />
              {editingForme ? 'Modifier la forme juridique' : 'Nouvelle forme juridique'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Form>
              <Form.Group className="mb-3">
                <Form.Label className="fw-bold small">
                  Code <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ex: SA, SARL, EURL..."
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  isInvalid={!!formErrors.code}
                  className="border-0 bg-light py-2 text-uppercase"
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.code?.[0]}
                </Form.Control.Feedback>
                <Form.Text className="text-muted small">
                  Code abrégé de la forme juridique (ex: SA, SARL, EURL)
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold small">
                  Libellé <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ex: Société Anonyme, Société à Responsabilité Limitée..."
                  value={formData.libelle}
                  onChange={(e) => setFormData({ ...formData, libelle: e.target.value })}
                  isInvalid={!!formErrors.libelle}
                  className="border-0 bg-light py-2"
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.libelle?.[0]}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold small">
                  Pays <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Ex: France, Belgique, Suisse..."
                  value={formData.pays}
                  onChange={(e) => setFormData({ ...formData, pays: e.target.value })}
                  isInvalid={!!formErrors.pays}
                  className="border-0 bg-light py-2"
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.pays?.[0]}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-bold small">
                  Description
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Description de la forme juridique (optionnel)..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  isInvalid={!!formErrors.description}
                  className="border-0 bg-light py-2"
                />
                <Form.Control.Feedback type="invalid">
                  {formErrors.description?.[0]}
                </Form.Control.Feedback>
              </Form.Group>

              {editingForme && (
                <Alert variant="info" className="mb-0">
                  <small>
                    <strong>ID:</strong> {editingForme.id}
                  </small>
                </Alert>
              )}
            </Form>
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Annuler
            </Button>
            <Button 
              variant="primary" 
              onClick={handleSubmit}
              disabled={saving || !formData.code.trim() || !formData.libelle.trim() || !formData.pays.trim()}
            >
              {saving ? (
                <Spinner animation="border" size="sm" className="me-2" />
              ) : (
                <CheckCircle size={16} className="me-2" />
              )}
              {editingForme ? 'Mettre à jour' : 'Créer'}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal 
          show={showDeleteModal} 
          onHide={() => setShowDeleteModal(false)}
          centered
        >
          <Modal.Header closeButton className="border-0" style={{ background: 'linear-gradient(135deg, #dc3545, #c82333)' }}>
            <Modal.Title className="text-white d-flex align-items-center">
              <Trash className="me-2" size={24} />
              Confirmer la suppression
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <p>
              Êtes-vous sûr de vouloir supprimer la forme juridique{' '}
              <strong>"{deletingForme?.libelle}"</strong> ?
            </p>
            <p className="text-muted small">
              Code: <strong>{deletingForme?.code}</strong>
              {deletingForme?.pays && ` • Pays: ${deletingForme.pays}`}
            </p>
            <Alert variant="warning" className="mb-0">
              <small>
                ⚠️ Cette action est irréversible. La suppression de cette forme juridique 
                pourrait affecter les contribuables qui y sont associés.
              </small>
            </Alert>
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Annuler
            </Button>
            <Button 
              variant="danger" 
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? (
                <Spinner animation="border" size="sm" className="me-2" />
              ) : null}
              Supprimer
            </Button>
          </Modal.Footer>
        </Modal>

        <style>{`
          .main-title {
            font-size: 1.5rem;
            font-weight: 700;
            color: #1a1a2e;
          }
          .breadcrumb {
            background: transparent;
            padding: 0;
            margin: 0;
          }
          .breadcrumb-item a {
            color: #6c757d;
            text-decoration: none;
          }
          .breadcrumb-item.active {
            color: #0d6efd;
          }
          .table > :not(caption) > * > * {
            padding: 0.75rem 0.75rem;
          }
          .form-control:focus, .form-select:focus {
            border-color: #0d6efd;
            box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
          }
          .btn-sm {
            padding: 0.25rem 0.5rem;
          }
          .pagination .page-link {
            padding: 0.375rem 0.75rem;
            font-size: 0.875rem;
          }
        `}</style>

        <Footer />
      </div>
    </>
  );
};

export default FormesJuridiques;